import clsx from "clsx";
import { useState } from "react";
import { Link } from "react-router";
import { describeError } from "@/api/client";
import { useCanEdit, useWorkspace } from "@/api/session";
import {
  Badge,
  Button,
  DocumentLink,
  Drawer,
  InlineAlert,
  Money,
  NumberField,
  RelativeTime,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  useToast,
} from "@/components";
import { formatInteger, MISSING_VALUE } from "@/lib/format";
import { countryName } from "@/lib/regions";
import {
  useAddToList,
  useDismissPriceCheck,
  useSetItemSpecs,
  useSupplyCategories,
  useSupplyItem,
  useUpdateListing,
  type SpecDetail,
  type SpecValue,
  type SupplyDetail,
} from "../api";
import {
  documentLabel,
  FITTING_CATEGORIES,
  formatSpec,
  madeInLabel,
  neckFinishLabel,
  perUnitLabel,
  priceCheckText,
  sourceLabel,
  unitPriceDecimals,
  unitSymbol,
} from "../format";
import { OfferPrice, otherMarketNote } from "./OfferPrice";
import styles from "./SupplyDrawer.module.css";

interface SupplyDrawerProps {
  itemId: string | null;
  onClose: () => void;
}

/** One supply: its offer, every spec with where it was read, other sizes and packs of the product,
 * what fits it, and corrections by hand. */
export function SupplyDrawer({ itemId, onClose }: SupplyDrawerProps) {
  const item = useSupplyItem(itemId);
  // The Undo toast outlives the drawer's contents (they unmount as the drawer closes), so its
  // mutation and callbacks live here.
  const restore = useUpdateListing();
  const toast = useToast();

  function hidden(detail: SupplyDetail) {
    onClose();
    toast.success("Hidden from supplies", {
      description: `${detail.title} isn’t a supply.`,
      action: {
        label: "Undo",
        onClick: () =>
          restore.mutate(
            { id: detail.listing.id, excluded: false },
            {
              onSuccess: () => toast.success("Back in supplies", { description: detail.title }),
              onError: (error) => toast.error("Couldn’t undo it", { description: describeError(error) }),
            },
          ),
      },
    });
  }

  return (
    <Drawer
      open={itemId !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      side="end"
      size="md"
      title={item.data?.title ?? "Supply"}
    >
      <div className={styles.body}>
        {item.isError ? (
          <InlineAlert tone="danger" title="Couldn’t load this supply">
            {describeError(item.error)}
          </InlineAlert>
        ) : null}
        {item.isPending && itemId ? <Skeleton shape="block" /> : null}
        {item.data ? <SupplyDetails key={item.data.id} detail={item.data} onHidden={hidden} /> : null}
      </div>
    </Drawer>
  );
}

function SupplyDetails({ detail, onHidden }: { detail: SupplyDetail; onHidden: (detail: SupplyDetail) => void }) {
  const canEdit = useCanEdit();
  const toast = useToast();
  const workspace = useWorkspace();
  const country = workspace?.ship_to.country || workspace?.market_country || "CA";
  const addToList = useAddToList();
  const [quantity, setQuantity] = useState<string | null>("1");
  const [editing, setEditing] = useState(false);
  const neck = detail.specs.neck_finish;
  const fitting = typeof neck === "string" ? (FITTING_CATEGORIES[detail.category] ?? []) : [];
  const taxonomy = useSupplyCategories();
  const categoryLabel = (slug: string) => taxonomy.data?.find((entry) => entry.slug === slug)?.label ?? slug;
  const marketNote = otherMarketNote(detail.offer, workspace?.market_country || "CA");
  const listedElsewhere =
    detail.offer.listed_price_minor !== null &&
    detail.offer.listed_currency !== "" &&
    detail.offer.listed_currency !== detail.offer.currency;

  function add() {
    const count = Math.max(1, Number(quantity ?? "1") || 1);
    addToList.mutate(
      { variant_id: detail.variant_id, quantity: count },
      {
        onSuccess: () =>
          toast.success(`Added ${formatInteger(count)} to your shopping list`, { description: detail.title }),
        onError: (error) => toast.error("Couldn’t add it to the list", { description: describeError(error) }),
      },
    );
  }

  return (
    <>
      <header className={styles.header}>
        <p className={styles.meta}>
          {[detail.variant_title, detail.sku].filter(Boolean).join(" · ")}
          {detail.variant_title || detail.sku ? " · " : ""}
          {detail.supplier.name}
          {detail.supplier.country ? ` (${countryName(detail.supplier.country)})` : ""}
        </p>
        <div className={styles.badges}>
          <Badge>{categoryLabel(detail.category)}</Badge>
          {detail.offer.in_stock === null ? (
            <Badge dashed>Stock unknown</Badge>
          ) : detail.offer.in_stock ? (
            <Badge tone="success">In stock</Badge>
          ) : (
            <Badge>Out of stock</Badge>
          )}
          {detail.supplier.ships_to_workspace === false ? (
            <Badge tone="warning">Doesn’t ship to {countryName(country)}</Badge>
          ) : null}
          {detail.us_glass_surtax ? <Badge tone="warning">US-made glass: 50% surtax</Badge> : null}
        </div>
        {detail.url || detail.one_pack_cart_url ? (
          <p className={styles.storeLinks}>
            {detail.url ? (
              <a className={styles.storeLink} href={detail.url} target="_blank" rel="noreferrer">
                Open in the store ↗
              </a>
            ) : null}
            {detail.one_pack_cart_url ? (
              <a className={styles.storeLink} href={detail.one_pack_cart_url} target="_blank" rel="noreferrer">
                Open a cart with 1 pack ↗
              </a>
            ) : null}
          </p>
        ) : null}
      </header>

      <section className={styles.offer} aria-label="Offer">
        <dl className={styles.facts}>
          <div>
            <dt>Pack price</dt>
            <dd>
              <OfferPrice offer={detail.offer} />
              {listedElsewhere ? (
                <span className={styles.listed}>
                  <Money minor={detail.offer.listed_price_minor} currency={detail.offer.listed_currency} /> at the store
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>{detail.unit_basis === "piece" ? "Per pack" : "Pack size"}</dt>
            <dd>{packContents(detail)}</dd>
          </div>
          <div>
            <dt>{perUnitLabel(detail.unit_basis)}</dt>
            <dd>
              {detail.unit_price_minor !== null ? (
                <Money
                  minor={detail.unit_price_minor}
                  currency={detail.offer.currency}
                  extraDecimals={unitPriceDecimals(detail.unit_basis)}
                />
              ) : (
                MISSING_VALUE
              )}
            </dd>
          </div>
          <div>
            <dt>Checked</dt>
            <dd>
              <RelativeTime value={detail.offer.observed_at} />
            </dd>
          </div>
        </dl>
        {detail.offer.note ? <p className={styles.note}>{detail.offer.note}</p> : null}
        {marketNote ? <p className={styles.note}>{marketNote}</p> : null}
        {detail.notes.map((note) => (
          <p key={note} className={styles.warning}>
            {note}
          </p>
        ))}
        {detail.price_check ? (
          <PriceCheckAlert detail={detail} canEdit={canEdit} onCorrect={() => setEditing(true)} />
        ) : null}
        {canEdit ? (
          <div className={styles.addRow}>
            <NumberField
              label="Packs"
              size="sm"
              min={1}
              step={1}
              decimals={0}
              value={quantity}
              onValueChange={setQuantity}
              className={styles.quantity}
            />
            <Button variant="primary" icon="cart" onClick={add} pending={addToList.isPending} pendingLabel="Adding…">
              Add to shopping list
            </Button>
          </div>
        ) : null}
      </section>

      {fitting.length ? (
        <section className={styles.section} aria-label="What fits">
          <h4 className={styles.sectionTitle}>Fits {neckFinishLabel(String(neck))}</h4>
          <p className={styles.links}>
            {fitting.map((slug) => (
              <Link
                key={slug}
                className={styles.link}
                to={`/supplies?category=${slug}&neck_finish=${encodeURIComponent(String(neck))}`}
              >
                {categoryLabel(slug)} with this neck finish
              </Link>
            ))}
          </p>
        </section>
      ) : null}

      <section className={styles.section} aria-label="Specs">
        <div className={styles.sectionHead}>
          <h4 className={styles.sectionTitle}>Specs</h4>
          {canEdit && !editing ? (
            <Button variant="ghost" size="sm" icon="pencil" onClick={() => setEditing(true)}>
              Correct…
            </Button>
          ) : null}
        </div>
        {editing ? (
          <SpecEditor detail={detail} onDone={() => setEditing(false)} />
        ) : (
          <table className={styles.specs}>
            <tbody>
              {detail.specs_detail.map((spec) => (
                <SpecRow key={spec.key} spec={spec} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {detail.add_ons.length ? (
        <section className={styles.section} aria-label="Add-ons">
          <h4 className={styles.sectionTitle}>Add-ons</h4>
          <p className={styles.note}>Closures the store offers to order with it. Searches by these specs find it.</p>
          {detail.add_ons.map((group) => (
            <div key={group.name} className={styles.addOnGroup}>
              <h5 className={styles.addOnName}>{group.name}</h5>
              <ul className={styles.addOns}>
                {group.choices.map((choice) => (
                  <li key={choice.label} className={styles.addOn}>
                    {choice.item_id ? (
                      <Link className={styles.link} to={`?${siblingParams(choice.item_id)}`} replace>
                        {choice.label}
                      </Link>
                    ) : (
                      <span>{choice.label}</span>
                    )}
                    <span className={styles.addOnSpecs}>
                      {choice.specs.map((spec) => spec.value_label).join(" · ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ) : null}

      {detail.siblings.length ? (
        <section className={styles.section} aria-label="Other sizes and packs">
          <h4 className={styles.sectionTitle}>Other sizes and packs</h4>
          <ul className={styles.siblings}>
            {detail.siblings.map((row) => (
              <li key={row.id} className={styles.sibling}>
                <Link className={styles.link} to={`?${siblingParams(row.id)}`} replace>
                  {row.variant_title || row.sku || row.title}
                </Link>
                <span className={styles.siblingPrice}>
                  <OfferPrice offer={row.offer} />
                  {row.unit_price_minor !== null ? (
                    <span className={styles.perPiece}>
                      <Money
                        minor={row.unit_price_minor}
                        currency={row.offer.currency}
                        extraDecimals={unitPriceDecimals(row.unit_basis)}
                        per={unitSymbol(row.unit_basis)}
                      />
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={styles.section} aria-label="How it was read">
        <h4 className={styles.sectionTitle}>Where this comes from</h4>
        <p className={styles.note}>
          {detail.listing.reason}
          {detail.listing.category_path ? ` (${detail.listing.category_path})` : ""}.
        </p>
        {detail.listing.documents.length ? (
          <ul className={styles.documents}>
            {detail.listing.documents.map((doc) => (
              <li key={doc.url}>
                <DocumentLink url={doc.url} copy={doc.copy} className={styles.link}>
                  {documentLabel(doc)} ↗
                </DocumentLink>
              </li>
            ))}
          </ul>
        ) : null}
        {detail.description ? <p className={styles.description}>{detail.description}</p> : null}
        {canEdit ? <ListingControls detail={detail} onHidden={onHidden} /> : null}
      </section>
    </>
  );
}

/** A price far off comparable supplies': correct the pack size, or mark the price as right. */
function PriceCheckAlert({
  detail,
  canEdit,
  onCorrect,
}: {
  detail: SupplyDetail;
  canEdit: boolean;
  onCorrect: () => void;
}) {
  const dismiss = useDismissPriceCheck(detail.id);
  const toast = useToast();
  if (!detail.price_check) return null;
  return (
    <InlineAlert
      tone="warning"
      title="Check the pack size"
      action={
        canEdit ? (
          <span className={styles.alertActions}>
            <Button size="sm" icon="pencil" onClick={onCorrect}>
              Correct…
            </Button>
            <Button
              size="sm"
              variant="ghost"
              pending={dismiss.isPending}
              pendingLabel="Saving…"
              onClick={() =>
                dismiss.mutate(undefined, {
                  onSuccess: () => toast.success("Marked as right", { description: detail.title }),
                  onError: (error) => toast.error("Couldn’t save that", { description: describeError(error) }),
                })
              }
            >
              Looks right
            </Button>
          </span>
        ) : null
      }
    >
      {priceCheckText(detail.price_check)}
    </InlineAlert>
  );
}

function siblingParams(id: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set("item", id);
  return params.toString();
}

/** What one pack holds: pieces, or the volume or mass of what is sold by volume or mass. A category
 * that doesn't count pieces per pack sells one piece. */
function packContents(detail: SupplyDetail): string {
  if (detail.unit_basis === "piece") {
    if (detail.pack_count) return formatInteger(detail.pack_count);
    return detail.specs_detail.some((spec) => spec.key === "pack_count") ? MISSING_VALUE : "1";
  }
  const amount = detail.specs_detail.find(
    (spec) => (spec.key === "pack_volume_ml" || spec.key === "pack_mass_g") && spec.value !== null,
  );
  return amount ? specText(amount) : MISSING_VALUE;
}

function specText(spec: SpecDetail): string {
  const value = spec.value ?? "";
  if (spec.kind !== "enum") return formatSpec({ key: spec.key, kind: spec.kind, unit: spec.unit, values: [] }, value);
  if (spec.key === "neck_finish") return neckFinishLabel(String(value));
  if (spec.key === "made_in") return madeInLabel(String(value));
  return spec.value_label ?? String(value);
}

function SpecRow({ spec }: { spec: SpecDetail }) {
  const known = spec.value !== null && spec.value !== undefined;
  const evidence = spec.text ? `“${spec.text}”` : sourceLabel(spec.source);
  const offered = spec.add_on_values.map((value) => value.label).join(", ");
  return (
    <tr>
      <th scope="row" className={styles.specName}>
        {spec.label}
      </th>
      <td className={styles.specValue}>
        {known ? (
          <>
            {specText(spec)}
            {offered ? <span className={styles.addOnValues}>Add-on: {offered}</span> : null}
          </>
        ) : spec.unresolved_values ? (
          <Tooltip content={spec.unresolved_text}>
            <button type="button" className={clsx(styles.hint, styles.unknown)}>
              Several: {spec.unresolved_values}
            </button>
          </Tooltip>
        ) : offered ? (
          offered
        ) : spec.applies ? (
          <span className={styles.unknown}>Not stated</span>
        ) : (
          <span className={styles.unknown}>Doesn’t apply</span>
        )}
      </td>
      <td className={styles.specSource}>
        {spec.manual ? (
          <Badge tone="info">By hand</Badge>
        ) : !known && offered ? (
          <Tooltip content="Not included: the store offers it to order with this (see Add-ons)">
            <button type="button" className={styles.hint}>
              Add-on
            </button>
          </Tooltip>
        ) : known ? (
          spec.url ? (
            <Tooltip content={evidence}>
              <a className={styles.link} href={spec.copy_url || spec.url} target="_blank" rel="noreferrer">
                {sourceLabel(spec.source)} ↗
              </a>
            </Tooltip>
          ) : (
            <Tooltip content={evidence}>
              <button type="button" className={styles.hint}>
                {sourceLabel(spec.source)}
              </button>
            </Tooltip>
          )
        ) : null}
      </td>
    </tr>
  );
}

function SpecEditor({ detail, onDone }: { detail: SupplyDetail; onDone: () => void }) {
  const taxonomy = useSupplyCategories();
  const save = useSetItemSpecs(detail.id);
  const toast = useToast();
  const defs = new Map(
    (taxonomy.data?.find((entry) => entry.slug === detail.category)?.facets ?? []).map((facet) => [facet.key, facet]),
  );
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(detail.specs_detail.map((spec) => [spec.key, spec.value === null ? "" : String(spec.value)])),
  );

  function submit() {
    const changes: Record<string, SpecValue | null> = {};
    for (const spec of detail.specs_detail) {
      const before = spec.value === null ? "" : String(spec.value);
      const after = values[spec.key] ?? "";
      if (after === before) continue;
      if (after === "") changes[spec.key] = null;
      else if (spec.kind === "boolean") changes[spec.key] = after === "true";
      else changes[spec.key] = after;
    }
    if (Object.keys(changes).length === 0) {
      onDone();
      return;
    }
    save.mutate(changes, {
      onSuccess: () => {
        toast.success("Specs saved", { description: "Your values stick when the store is crawled again." });
        onDone();
      },
      onError: (error) => toast.error("Couldn’t save the specs", { description: describeError(error) }),
    });
  }

  return (
    <form
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {detail.specs_detail.map((spec) => {
        const facet = defs.get(spec.key);
        const value = values[spec.key] ?? "";
        const set = (next: string | null) => setValues((current) => ({ ...current, [spec.key]: next ?? "" }));
        if (spec.kind === "number") {
          return (
            <NumberField
              key={spec.key}
              label={spec.label}
              size="sm"
              min={0}
              unit={spec.unit}
              value={value || null}
              onValueChange={set}
            />
          );
        }
        if (spec.kind === "boolean" || facet?.values.length) {
          const options =
            spec.kind === "boolean"
              ? [
                  { value: "true", label: "Yes" },
                  { value: "false", label: "No" },
                ]
              : (facet?.values ?? []);
          return (
            <Select
              key={spec.key}
              label={spec.label}
              size="sm"
              options={[{ value: "", label: "Not stated" }, ...options]}
              value={value}
              onValueChange={set}
            />
          );
        }
        return <TextField key={spec.key} label={spec.label} size="sm" value={value} onValueChange={set} />;
      })}
      <div className={styles.editorActions}>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" pending={save.isPending} pendingLabel="Saving…">
          Save specs
        </Button>
      </div>
    </form>
  );
}

function ListingControls({ detail, onHidden }: { detail: SupplyDetail; onHidden: (detail: SupplyDetail) => void }) {
  const taxonomy = useSupplyCategories();
  const update = useUpdateListing();
  const toast = useToast();
  const options = (taxonomy.data ?? [])
    .filter((entry) => entry.includes.length === 0)
    .map((entry) => ({ value: entry.slug, label: entry.label }));
  return (
    <div className={styles.listingControls}>
      <Select
        label="Category"
        size="sm"
        options={options}
        value={detail.listing.category}
        onValueChange={(value) =>
          value &&
          value !== detail.listing.category &&
          update.mutate(
            { id: detail.listing.id, category: value },
            {
              onSuccess: () => toast.success("Category changed", { description: "Its specs were read again." }),
              onError: (error) => toast.error("Couldn’t change the category", { description: describeError(error) }),
            },
          )
        }
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          update.mutate(
            { id: detail.listing.id, excluded: true },
            {
              onSuccess: () => onHidden(detail),
              onError: (error) => toast.error("Couldn’t hide it", { description: describeError(error) }),
            },
          )
        }
      >
        Not a supply
      </Button>
    </div>
  );
}
